import { ChevronRight, Settings2 } from 'lucide-react';
import { FC, useMemo, useState } from 'react';

import { ItemFilter } from '@repo/ui/components/custom/navigation-filters/item-filter/component';
import {
  SliderControl,
  type SliderControlsOnReady,
} from '@repo/ui/components/custom/navigation-filters/slider-control/component';
import { Progress } from '@repo/ui/components/ui/progress';
import { cn } from '@repo/ui/lib/utils';
import { useCandidates } from '@/state';
import { permissions as permifyPermissions } from '../../../../../api/src/permify/constants';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion';
import { Button } from '../../ui/button';
import {
  Sheet,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetPrimitive,
  SheetTitle,
  SheetTrigger,
  sheetVariants,
} from '../../ui/sheet';
import { Switch } from '../../ui/switch';
import {
  getFilterSummaryKey,
  type NavigationFilterDefinition,
} from '../../../helpers/navigation-filter-definitions';

type NavigationFiltersProps = {
  className?: string;
  disabled?: boolean;
  filterDefinitions?: NavigationFilterDefinition[];
  onClick?: () => void;
  openCallId: string;
};

export const NavigationFilters: FC<NavigationFiltersProps> = ({
  className,
  disabled,
  filterDefinitions = [],
  onClick,
  openCallId,
}) => {
  const [expandedFilter, setExpandedFilter] = useState<string>();
  const [sliderStates, setSliderStates] = useState<
    Record<string, SliderControlsOnReady | undefined>
  >({});
  const {
    loadingSpinner,
    params,
    areDefaultFiltersActive,
    resetFilters,
    updateFilters,
    summary: candidateSummary,
  } = useCandidates();

  const profileFilters = useMemo(
    () =>
      filterDefinitions.filter(
        (definition) => (definition.group ?? 'profile') === 'profile',
      ),
    [filterDefinitions],
  );
  const attributeFilters = useMemo(
    () =>
      filterDefinitions.filter(
        (definition) => definition.group === 'attributes',
      ),
    [filterDefinitions],
  );

  const fieldMap = (fieldName: string) =>
    Object.entries(
      (candidateSummary[fieldName as keyof typeof candidateSummary] ||
        {}) as Record<string, number>,
    ).map(([value, count]) => ({
      numberOfCandidates: count,
      value: value,
      label: value,
    }));

  const setSliderState =
    (key: string) => (state: SliderControlsOnReady | undefined) =>
      setSliderStates((current) => ({ ...current, [key]: state }));

  const closeSliderStates = () => {
    Object.values(sliderStates).forEach((state) => state?.close());
  };

  const renderMultiSelectFilter = (definition: NavigationFilterDefinition) => {
    const key = String(definition.key);
    const summaryKey = String(getFilterSummaryKey(definition));
    const values = fieldMap(summaryKey);
    const selected = (params.filters as Record<string, unknown>)[key];

    return (
      <AccordionItem key={key} value={key}>
        <AccordionTrigger className="text-xs font-medium hover:no-underline">
          {definition.label}
        </AccordionTrigger>
        <AccordionContent
          wrapperClassName={cn({
            'overflow-visible': expandedFilter === key,
          })}
        >
          <ItemFilter
            unit={definition.unit ?? definition.label.toLowerCase()}
            values={values}
            featuredValues={definition.featuredValues}
            selected={(selected || []) as string[]}
            onChange={(value) =>
              updateFilters((f) => ({
                ...f,
                openCallId,
                [definition.key]: value,
              }))
            }
          />
        </AccordionContent>
      </AccordionItem>
    );
  };

  const renderRangeFilter = (definition: NavigationFilterDefinition) => {
    const key = String(definition.key);
    const title = definition.sliderTitle ?? key;
    const range = candidateSummary[
      getFilterSummaryKey(definition) as keyof typeof candidateSummary
    ] as { range?: number[] } | undefined;
    const [from, to] = range?.range || [];
    const selected = ((params.filters as Record<string, unknown>)[key] ||
      []) as string[];

    return (
      <AccordionItem key={key} value={key}>
        <AccordionTrigger
          className="text-xs font-medium hover:no-underline"
          onClick={sliderStates[key]?.close}
        >
          {definition.label}
        </AccordionTrigger>
        <AccordionContent wrapperClassName="overflow-visible">
          <SliderControl
            className={cn({
              'opacity-0': expandedFilter !== key,
            })}
            title={title}
            unit={definition.rangeUnit}
            onReady={setSliderState(key)}
            from={Number(from || 0)}
            to={Number(to || 0)}
            filterFrom={Number(selected[0] || from || 0)}
            filterTo={Number(selected[1] || to || 0)}
            onChange={([rangeFrom, rangeTo]) => {
              updateFilters((f) => ({
                ...f,
                openCallId,
                [definition.key]: [String(rangeFrom), String(rangeTo)],
              }));
            }}
          />
        </AccordionContent>
      </AccordionItem>
    );
  };

  const renderToggleFilter = (definition: NavigationFilterDefinition) => {
    const key = String(definition.key);

    return (
      <section
        key={key}
        className={cn(
          'flex flex-row justify-between items-center h-14 border-b border-cn-border',
          {
            'opacity-40':
              !candidateSummary[
                getFilterSummaryKey(definition) as keyof typeof candidateSummary
              ],
          },
        )}
      >
        <label
          htmlFor={key}
          className="text-xs font-medium text-cn-muted-foreground"
        >
          {definition.label}
        </label>
        <Switch
          id={key}
          checked={Boolean((params.filters as Record<string, unknown>)[key])}
          onCheckedChange={(checked) =>
            updateFilters((f) => ({
              ...f,
              openCallId,
              [definition.key]: checked,
            }))
          }
        />
      </section>
    );
  };

  const renderAccordionFilter = (definition: NavigationFilterDefinition) => {
    if (definition.kind === 'range') return renderRangeFilter(definition);
    if (definition.kind === 'multiSelect')
      return renderMultiSelectFilter(definition);
    return null;
  };

  return (
    <Sheet onOpenChange={closeSliderStates}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          data-testid="navigation-filters-button"
          data-permission={permifyPermissions.openCalls.filterAndSearch}
          className={className}
          disabled={disabled}
          onClick={onClick}
        >
          <span className="hidden md:block">Filter</span>
          <Settings2 />
        </Button>
      </SheetTrigger>
      <SheetPortal>
        <SheetOverlay className="bg-black/20" />
        <SheetPrimitive.Content
          className={cn(
            sheetVariants({ side: 'right' }),
            'w-full sm:max-w-sm p-0 flex flex-col gap-0',
          )}
        >
          <SheetHeader className="flex-0 space-y-0">
            <SheetDescription className="hidden">
              Navigation filter
            </SheetDescription>
            <div className="min-h-2">{loadingSpinner && <Progress />}</div>

            <section className="px-2 flex flex-row justify-between items-center border-b border-cn-border">
              <SheetPrimitive.Close asChild>
                <button className="w-9 h-9 flex items-center justify-center text-cn-muted-foreground">
                  <ChevronRight />
                </button>
              </SheetPrimitive.Close>

              <SheetTitle className="flex flex-row text-cn-muted-foreground text-xs font-medium">
                Filters
                <Settings2 className="ml-2 w-4 h-4 mr-3" />
              </SheetTitle>
            </section>
          </SheetHeader>

          <section className="px-4 flex-1 overflow-y-scroll">
            {profileFilters
              .filter((definition) => definition.kind === 'toggle')
              .map(renderToggleFilter)}
            {profileFilters.some(
              (definition) => definition.kind !== 'toggle',
            ) && (
              <Accordion
                type="single"
                value={expandedFilter}
                onValueChange={setExpandedFilter}
                className="w-full text-cn-muted-foreground"
                collapsible
              >
                {profileFilters
                  .filter((definition) => definition.kind !== 'toggle')
                  .map(renderAccordionFilter)}
              </Accordion>
            )}

            {attributeFilters.length > 0 && (
              <>
                <hr className="bg-cn-border my-2 -mx-4" />

                <section className="text-sm font-medium h-7 text-cn-sidebar-ring flex items-end">
                  Attributes
                </section>

                <Accordion
                  type="single"
                  value={expandedFilter}
                  onValueChange={setExpandedFilter}
                  className="w-full text-cn-muted-foreground"
                  collapsible
                >
                  {attributeFilters.map(renderAccordionFilter)}
                </Accordion>

                <hr className="bg-cn-border -mx-4" />
              </>
            )}
          </section>

          <SheetFooter className="flex-0 flex-row space-x-2 px-2 py-2 border-t border-cn-border">
            <Button
              className="w-1/2"
              variant="outline"
              onClick={() => resetFilters(filterDefinitions)}
              disabled={areDefaultFiltersActive(filterDefinitions)}
            >
              Clear All
            </Button>

            <SheetPrimitive.Close asChild>
              <Button
                className="w-1/2"
                disabled={areDefaultFiltersActive(filterDefinitions)}
              >
                Show Results
              </Button>
            </SheetPrimitive.Close>
          </SheetFooter>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
};
